import { ClockCircleOutlined } from '@ant-design/icons';
import { ListItemSkeleton, Modal } from '@src/components/ui';
import { PageWrapper } from '@src/components';
import {
    useLazyGetNotificationsQuery,
    useMarkAsReadMutation,
} from '@src/services/notification/notificationApiSlice';
import { Notification } from '@src/services/notification/notificationApiSliceTypes';
import { List, Pagination } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaBell } from 'react-icons/fa6';

export function NotificationList() {
    const { t } = useTranslation();
    const [page, setPage] = useState(1);
    const [selectedNotification, setSelectedNotification] =
        useState<Notification | null>(null);

    const [getNotificationList, { data, isLoading }] =
        useLazyGetNotificationsQuery();
    const [markAsRead] = useMarkAsReadMutation();

    const notifications = data?.data || [];

    useEffect(() => {
        getNotificationList({ page, limit: 10 });
    }, [page]);

    const handleNotificationClick = async (item: Notification) => {
        setSelectedNotification(item);

        if (!item.isRead) {
            try {
                await markAsRead({ id: item.id });
                getNotificationList({ page, limit: 10 });
            } catch (err) {
                console.error('Failed to mark notification as read', err);
            }
        }
    };
    if (isLoading) return <ListItemSkeleton className="!min-h-0" />;

    return (
        <PageWrapper>
            {notifications.length ? (
                <div className="max-w-xl mx-auto p-4">
                    <List
                        itemLayout="horizontal"
                        dataSource={notifications}
                        renderItem={(item) => (
                            <button
                                className="cursor-pointer border-b hover:bg-gray-50 p-3 rounded-md"
                                onClick={() => handleNotificationClick(item)}
                            >
                                <div className="flex justify-between items-start">
                                    <div>
                                        <div className="font-medium text-gray-800">
                                            {item.title}
                                        </div>
                                        <div className="text-gray-500 text-xs flex items-center gap-1 mt-1">
                                            <ClockCircleOutlined className="text-[10px]" />
                                            {item.timestamp}
                                        </div>
                                    </div>
                                    {!item.isRead && (
                                        <span className="h-2.5 w-2.5 rounded-full bg-red-500 mt-1" />
                                    )}
                                </div>
                            </button>
                        )}
                    />

                    <div className={'flex justify-end mt-4'}>
                        <Pagination
                            current={data?.page ?? 1}
                            pageSize={data?.limit ?? 10}
                            total={data?.total ?? 0}
                            onChange={(newPage) => setPage(newPage)}
                            showSizeChanger={false}
                        />
                    </div>

                    {selectedNotification && (
                        <Modal
                            open={!!selectedNotification}
                            title={t('pages.notification.detailsTitle')}
                            onCancel={() => setSelectedNotification(null)}
                        >
                            <>
                                <p className="text-gray-700">
                                    {selectedNotification?.description}
                                </p>
                                <div className="text-xs text-gray-400 mt-4">
                                    Created at:{' '}
                                    {new Date(
                                        selectedNotification?.createdAt || ''
                                    ).toLocaleString()}
                                </div>
                            </>
                        </Modal>
                    )}
                </div>
            ) : (
                <div className="flex flex-col gap-6 items-center justify-center h-[294px]">
                    <div className="flex border items-center justify-center rounded-[48px] p-2 border-black-48 w-20 h-20">
                        <FaBell size={46} className="text-black-48" />
                    </div>
                    <h2 className="text-xl font-medium text-black-100">
                        No Notifications Yet
                    </h2>
                    <div className="font-medium text-base flex flex-col gap-2 items-center text-black-48">
                        <h3>You have no notifications right now.</h3>
                        <h3>Come back later</h3>
                    </div>
                </div>
            )}
        </PageWrapper>
    );
}
